import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

import { useCreateBlankDiagramMutation } from '../graphql/DiagramPersistence.generated';
import { useIsArtifactStudioAvailable } from '../hooks/useIsArtifactStudioAvailable';

/**
 * Creates an empty diagram in the household and opens it in the diagram
 * editor. Shows nothing when Artifact Studio is not available.
 */
export function CreateArtifactButton({ householdId }: { householdId: string }) {
  const isAvailable = useIsArtifactStudioAvailable();
  const { open: openEditor } = useTrowser(TrowserKey.Artifact);
  const [createBlankDiagram, { loading }] = useCreateBlankDiagramMutation();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  if (!isAvailable) return null;

  async function handleClick() {
    try {
      const { data } = await createBlankDiagram({
        variables: { householdID: householdId },
      });
      const artifactID = data?.createBlankDiagramArtifact.id;
      if (artifactID) openEditor({ target: artifactID });
    } catch (error) {
      reportError('Failed to create a blank diagram', error as Error);
      showFeedback('The diagram could not be created. Try again.', {
        variant: 'error',
      });
    }
  }

  return (
    <Button
      variant="primary"
      size="sm"
      startIcon={PlusIcon}
      loading={loading}
      onClick={() => {
        void handleClick();
      }}
    >
      Create artifact
    </Button>
  );
}
